import { useState, useRef, useCallback, useEffect } from "react";
import { findAndParseMrz, type MrzParseResult } from "../utils/mrz/mrz";
import { correctMrzLines } from "../utils/mrz/mrzOcrFixes";
import { guideInVideoPixels, prepareMrzCrop, MRZ_VARIANTS } from "../utils/mrz/mrzImage";
import { createReadAgreement } from "../utils/mrz/mrzReadAgreement";
import { isMrzOcrReady, recognizeMrzLines } from "../services/ocr/mrzOcrWorker";

// EN-4743. Reads a passport's MRZ from the live camera picture, like the kiosk's
// text-recognition frame processor (visitly-kiosk-app/src/components/IDCaptureModal.js,
// handleMrzFrameResult): passes run back to back while `active`, and the first
// read confirmed by two identical passes is kept (latched); later passes stop.
const MIN_PASS_INTERVAL_MS = 300;

interface UseMrzScannerOptions {
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  active: boolean;
  // Called once, the moment a confirmed read is latched.
  onConfirmed?: (result: MrzParseResult) => void;
}

export interface MrzScanStats {
  passes: number;
  readsPassingCheckDigits: number;
  distinctReads: number;
}

interface UseMrzScannerReturn {
  result: MrzParseResult | null;
  // Clears the kept read so the next active period reads from scratch.
  reset: () => void;
  getStats: () => MrzScanStats;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useMrzScanner = ({ videoEl, stageEl, guideEl, active, onConfirmed }: UseMrzScannerOptions): UseMrzScannerReturn => {
  const [result, setResult] = useState<MrzParseResult | null>(null);
  // Bumped by reset() so the reading loop starts again even if `active` didn't change.
  const [generation, setGeneration] = useState<number>(0);
  const latchedRef = useRef<MrzParseResult | null>(null);
  const agreementRef = useRef(createReadAgreement());
  const passesRef = useRef<number>(0);
  // Latest callback, so the running loop never calls a stale one.
  const onConfirmedRef = useRef(onConfirmed);
  useEffect(() => {
    onConfirmedRef.current = onConfirmed;
  }, [onConfirmed]);

  useEffect(() => {
    if (!active || !videoEl || !stageEl || !guideEl || latchedRef.current) return;
    let cancelled = false;
    const canvas = document.createElement("canvas");

    const loop = async () => {
      if (!(await isMrzOcrReady()) || cancelled) return;
      while (!cancelled && !latchedRef.current) {
        const started = performance.now();
        const region = guideInVideoPixels(videoEl, stageEl, guideEl);
        if (region) {
          const variant = MRZ_VARIANTS[passesRef.current % MRZ_VARIANTS.length];
          prepareMrzCrop(videoEl, region, variant.band, variant.width, canvas);
          const lines = await recognizeMrzLines(canvas);
          if (cancelled) return;
          passesRef.current += 1;
          const parsed = findAndParseMrz(correctMrzLines(lines));
          const confirmed = parsed ? agreementRef.current.add(parsed) : null;
          if (confirmed) {
            latchedRef.current = confirmed;
            setResult(confirmed);
            onConfirmedRef.current?.(confirmed);
            return;
          }
        }
        const wait = MIN_PASS_INTERVAL_MS - (performance.now() - started);
        if (wait > 0) await sleep(wait);
      }
    };
    void loop();

    return () => {
      cancelled = true;
    };
  }, [active, videoEl, stageEl, guideEl, generation]);

  const reset = useCallback(() => {
    latchedRef.current = null;
    agreementRef.current.reset();
    passesRef.current = 0;
    setResult(null);
    setGeneration((g) => g + 1);
  }, []);

  const getStats = useCallback(
    (): MrzScanStats => ({
      passes: passesRef.current,
      readsPassingCheckDigits: agreementRef.current.readsPassingCheckDigits,
      distinctReads: agreementRef.current.distinctReads,
    }),
    [],
  );

  return { result, reset, getStats };
};
